import { defineComponent, h } from 'vue'

import { QField, QOptionGroup, QRadio } from 'quasar'

import { bsComponentProps, useBsComponent } from '../useBsComponent'
import { useVm } from '../../../composables/useVm'
import { quasarHandlers } from '../quasarHandlers'

/**
 * One radio, or a group of them when `options` is passed as an attribute — the group gets the field
 * shell so its validation message has somewhere to render.
 *
 * `stopSaveOnFirstSet` is what keeps this from saving the moment a value is assigned: QOptionGroup
 * emits `input` as soon as it is given one, and without the flag every screen with a radio on it
 * saved itself on load.
 */
export default defineComponent({
  name: 'BsRadio',

  // See BsInput: undeclared DOM-named events are bound twice, once natively.
  emits: [ 'input', 'update:modelValue' ],

  props: {
    ...bsComponentProps,
    stacked: { type: Boolean, default: false },
    hideBottomSpace: { type: Boolean, default: false }
  },

  setup (props, { emit }) {
    const vm = useVm()
    const bs = useBsComponent({
      vm,
      props,
      emit,
      // This combination forces the auto save on each click
      autoSaveMethod: ''
    })

    bs.stopSaveOnFirstSet.value = true
    bs.forceTriggerOnChange.value = true

    return { ...bs }
  },

  render () {
    const props = {
      type: 'radio'
    }

    if (this.$attrs?.options) {
      return h(QField, {
        class: ['bs-radio-container', this.getClasses()],
        // so we get auto save classes applied.
        ...this.getValidationProps({
          borderless: true,
          bottomSlots: true,
          noErrorIcon: true,
          dense: true,
          stackLabel: true,
          hideBottomSpace: this.hideBottomSpace
        })
      }, {
          error: () => this.renderErrorSlot(),
          ...this.renderAutoSaveMessage(),
        default: () => [
        h(QOptionGroup, {
          // 'bs-radio' was a staticClass alongside this one; Vue 3 has a single class field.
          class: ['bs-radio', this.stacked ? 'bs-radio__stacked' : void 0],
          ...this.getRenderProps(props),
          ...quasarHandlers(this.getListeners()),
          ...this.getAttributes()
        }, { default: () => this.slot(this, 'default') })
      ]
      })
    } else {
      return h(QRadio, {
        class: 'bs-radio',
        ...this.getRenderProps(props),
        ...quasarHandlers(this.getListeners()),
        ...this.getAttributes()
      }, { default: () => this.slot(this, 'default') })
    }
  }
})
